import type {
  FeedAnchorRestoreRecord,
  FeedMessageJumpInput,
  FeedRestoreRecord
} from './session';
import type { ChannelId, ChatItem, FeedPage } from './types';

/** Resolves a restore anchor to a mounted item key, falling back to resolved/page-first ids with zero offset. */
export function resolveRestoreTarget(
  projectedMessages: readonly ChatItem[],
  page: Pick<FeedPage, 'resolvedId'>,
  savedRestore: FeedAnchorRestoreRecord
): { key: string; offsetPx: number } | undefined {
  const exactItem = projectedMessages.find((item) => item.id === savedRestore.id);
  const restoredKey = exactItem?.itemKey ?? page.resolvedId ?? projectedMessages[0]?.itemKey;
  if (!restoredKey) return undefined;

  return {
    key: restoredKey,
    offsetPx: exactItem ? savedRestore.offsetPx : 0
  };
}

/** Resolves message-jump anchors by sort order first, then id/resolved id fallback. */
export function resolveJumpTarget(
  projectedMessages: readonly ChatItem[],
  page: Pick<FeedPage, 'resolvedId'>,
  target: Pick<FeedMessageJumpInput, 'id' | 'sortOrder'>
): { key: string } | undefined {
  const sortOrderMatch = projectedMessages.find((item) => item.sortOrder === target.sortOrder);
  if (sortOrderMatch) return { key: sortOrderMatch.itemKey };

  const idMatch = projectedMessages.find((item) => item.id === target.id || item.id === page.resolvedId);
  if (idMatch) return { key: idMatch.itemKey };

  return undefined;
}

/** Drops restore records that do not belong to the current account/conversation or storage version. */
export function validatedRestoreRecord(
  record: FeedRestoreRecord | undefined,
  accountId: string,
  conversationId: ChannelId
): FeedRestoreRecord | undefined {
  if (!record) return undefined;
  if (record.version !== 1) return undefined;
  if (record.accountId !== accountId || record.conversationId !== conversationId) return undefined;
  if (!Number.isFinite(record.savedAt)) return undefined;
  if (record.mode === 'latest') return record;
  if (record.mode !== undefined && record.mode !== 'anchor') return undefined;
  if (typeof record.sortOrder !== 'string') return undefined;
  if (typeof record.itemKey !== 'string' || !Number.isFinite(record.offsetPx)) return undefined;
  return record;
}
